import { useMemo } from 'react'
import { useFirestoreGeneral } from '../../firebase/useFirestore'
import OutputData from '../graphData/event/OutputData'
import filterRowsByPeriod from '../../helpers/liveReportHelpers/filterRowsByPeriod'
import DashboardCardOptions from '../dashboard/DashboardCardOptions'
import DashboardPersonaFilter from '../dashboard/DashboardPersonaFilter'
import DashboardFilterBar from '../dashboard/DashboardFilterBar'
import { getDefaultGraphType } from '../../helpers/visualisationDefaults'
import LoadingSpinner from '../common/LoadingSpinner'
import useChartColors from '../../hooks/useChartColors'

// AI-CONTEXT: See docs/features/livereport-widgets-dashboard-parity.md
// Shared visualisation engine for a single Output (from the Outputs
// collection). Used by `OutputIndicatorCardContent` on the dashboard
// and by `OutputWidget` in the live-report editor.
//
// Renders: optional persona filter bar → OutputData. Optional
// DashboardCardOptions is passed through `OutputData`'s
// `dashboardOptions` slot when a dashboard item is supplied.
//
// templateOutputData: when set, the Output doc is resolved from the
// pre-fetched template blob instead of subscribing to Firestore, and
// the same blob is forwarded to OutputData (which has its own
// template-mode handling).

/**
 * @param {object} props
 * @param {string} props.outputId - Output document ID
 * @param {string} [props.graphType]
 * @param {string[]} [props.colorPalette]
 * @param {Object<string, string>} [props.colorOverrides]
 * @param {string} [props.graphColor]
 * @param {string} [props.lineColor]
 * @param {string} [props.barColor]
 * @param {Array<object>} [props.personas]
 * @param {boolean} [props.personasEnabled]
 * @param {string} [props.selectedPersonaId]
 * @param {(personaId: string) => void} [props.onPersonaChange]
 * @param {object|null} [props.dashboardItem]
 * @param {object|null} [props.templateOutputData]
 * @param {Date|null} [props.startDate] - Period lower bound for the live data layer
 * @param {Date|null} [props.endDate] - Period upper bound (end of day) for the live data layer
 * @param {{start: string|null, end: string|null}|null} [props.periodRange] - Resolved period as ISO dates; filters template-blob rows client-side
 * @param {number|string} [props.height] - Chart height passed to OutputData (e.g. '100%' to fill its box). Omitted callers keep OutputData's own default.
 * @param {string} [props.numberFontSize] - CSS font-size for the 'number' graph type.
 * @param {'left' | 'center' | 'right'} [props.numberTextAlign] - Alignment for the 'number' graph type.
 * @param {string} [props.numberFontWeight] - Font weight for the 'number' graph type.
 * @param {boolean} [props.hideControls] - Suppress this component's own chrome (persona filter + options footer) so a wrapping ComparisonFrame can own them once. See docs/features/compare-two-periods.md
 * @returns {JSX.Element}
 */
const OutputVisualization = ({
    outputId,
    graphType,
    colorPalette,
    colorOverrides,
    graphColor,
    lineColor,
    barColor,
    personas,
    personasEnabled,
    selectedPersonaId,
    onPersonaChange,
    dashboardItem = null,
    templateOutputData = null,
    startDate = null,
    endDate = null,
    periodRange = null,
    height,
    numberFontSize,
    numberTextAlign,
    numberFontWeight,
    hideControls = false,
}) => {
    const isTemplate = !!templateOutputData

    // Template mode: apply the resolved period client-side on the blob rows —
    // the blob is shared (and unfiltered) so the period stays a render-time
    // parameter. Live mode filters via startDate/endDate in the data layer.
    const periodTemplateOutputData = useMemo(() => {
        if (!templateOutputData || !periodRange) return templateOutputData
        return {
            ...templateOutputData,
            eventResults: filterRowsByPeriod(templateOutputData.eventResults || [], periodRange),
        }
    }, [templateOutputData, periodRange])

    const liveOutputs = useFirestoreGeneral('Outputs', 'ID', isTemplate ? '' : (outputId || ''))
    const output = isTemplate
        ? (templateOutputData?.outputs || []).find(o => o.ID === outputId)
        : (Array.isArray(liveOutputs) && liveOutputs.length > 0 ? liveOutputs[0] : null)

    const showPersonaFilter = Boolean(personasEnabled) && Array.isArray(personas) && personas.length > 0

    // Boven de early return: een hook moet elke render in dezelfde volgorde
    // draaien. Buiten een live rapportage telt de opgeslagen kaartkleur niet
    // meer mee; useChartColors geeft dan het vaste palet en laat overrides vallen.
    const { palette, overrides } = useChartColors({ colorPalette, colorOverrides })

    if (!output) return <LoadingSpinner />

    const resolvedGraphType = graphType || getDefaultGraphType('output')
    const color = overrides?.[output.ID] || palette[0]

    return (
        <>
            {!hideControls && showPersonaFilter && (
                <DashboardFilterBar
                    onClear={() => onPersonaChange && onPersonaChange('')}
                    activeFilterCount={selectedPersonaId ? 1 : 0}
                >
                    <DashboardPersonaFilter
                        personas={personas}
                        selectedPersonaId={selectedPersonaId || ''}
                        onChange={onPersonaChange}
                    />
                </DashboardFilterBar>
            )}
            <OutputData
                outputId={output.ID}
                graphType={resolvedGraphType}
                height={height}
                numberFontSize={numberFontSize}
                numberTextAlign={numberTextAlign}
                numberFontWeight={numberFontWeight}
                color={color}
                graphColor={graphColor}
                lineColor={lineColor}
                barColor={barColor}
                personaId={selectedPersonaId || null}
                startDate={startDate}
                endDate={endDate}
                templateOutputData={periodTemplateOutputData}
                dashboardOptions={(!hideControls && dashboardItem) ? (
                    <DashboardCardOptions
                        item={dashboardItem}
                        fieldType='output'
                        currentGraphType={resolvedGraphType}
                        subItems={[{ ID: output.ID, label: output.Title || output.Output || 'Output' }]}
                    />
                ) : null}
            />
        </>
    )
}

export default OutputVisualization
